import { useState } from "react";
import { useLocation } from "wouter";
import { ArrowRight, Loader2, Sparkle } from "lucide-react";
import { ASSISTANTS, sentFromLabel, snoozeUntil, type UnfoldingDraft } from "@workspace/assistants";
import { useAuth } from "@/hooks/useAuth";
import { useSnoozeThought, useUnfoldThought, useWaitingThoughts } from "@/hooks/use-assistants";
import { INK, MUTED, PURPLE } from "@/components/assistant/assistant-ui";
import { cn } from "@/lib/utils";

/** The newest thought an assistant sent to wait on Home, and how many more are behind it. */
export function useWaitingThought(): { thought: UnfoldingDraft; more: number } | null {
  const { isAuthenticated } = useAuth();
  const { data } = useWaitingThoughts();
  if (!isAuthenticated || !data || data.length === 0) return null;
  return { thought: data[0], more: data.length - 1 };
}

/**
 * AI 14: the thought waiting in Home's input box, marked as sent from Claude. "Unfold
 * this" makes it an Unfolding like any other; "Not now" puts it away until tomorrow.
 */
export function WaitingThoughtCard({ thought, more }: { thought: UnfoldingDraft; more: number }) {
  const [, setLocation] = useLocation();
  const unfold = useUnfoldThought();
  const snooze = useSnoozeThought();
  const [error, setError] = useState<string | null>(null);
  const name = ASSISTANTS[thought.assistant].name;

  async function onUnfold() {
    setError(null);
    try {
      const { sessionId } = await unfold.mutateAsync(thought.id);
      setLocation(`/processing/${sessionId}`);
    } catch (err) {
      setError((err as Error).message || "Couldn't unfold this right now. Try again in a moment.");
    }
  }

  return (
    <div className="relative z-10 flex min-h-[244px] w-full flex-col gap-3 rounded-[24px] bg-white p-[22px] text-left shadow-[0_18px_20px_rgba(28,21,48,0.25)]">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded-[14px] border border-[rgba(47,41,37,0.12)] bg-white px-2.5 py-1 text-xs font-medium ${INK}`}>Link</span>
        <span className="rounded-[14px] bg-[#2f2925] px-2.5 py-1 text-xs font-medium text-white">Text</span>
        <span className="flex-1" />
        <span className={`inline-flex items-center gap-1.5 rounded-[14px] bg-[#e9e2f6] px-2.5 py-1 text-xs font-medium ${PURPLE}`}>
          <Sparkle className="size-[13px]" />
          {sentFromLabel(thought)}
        </span>
      </div>

      <p className={cn(`whitespace-pre-wrap break-words ${INK}`, thought.text.length > 220 ? "text-base leading-7" : "text-[20px] leading-[30px]")}>
        {thought.text}
      </p>

      <div className="flex-1" />

      <div className="flex flex-wrap items-center gap-3">
        <p className={`min-w-[12rem] flex-1 text-xs leading-[17px] ${MUTED}`}>
          Waiting for you. Only this thought came across; your chat stayed in {name}.
          {more > 0 && ` ${more} more waiting.`}
        </p>
        <button
          type="button"
          disabled={snooze.isPending}
          onClick={() => snooze.mutate({ id: thought.id, until: snoozeUntil(new Date()) })}
          className={`rounded-[22px] px-0.5 py-[11px] text-[13px] font-semibold ${PURPLE} hover:underline disabled:opacity-50`}
        >
          Not now
        </button>
        <button
          type="button"
          onClick={() => void onUnfold()}
          disabled={unfold.isPending}
          className="inline-flex items-center gap-2 rounded-[22px] bg-[#6e53ac] px-5 py-[11px] text-sm font-semibold text-white transition-colors hover:bg-[#6e53ac]/90 disabled:opacity-60"
        >
          Unfold this
          {unfold.isPending ? <Loader2 className="size-[15px] animate-spin" /> : <ArrowRight className="size-[15px]" />}
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
